<template>
  <aside
    v-if="!fechada"
    ref="barra"
    class="barra-transicao"
    :aria-label="t('compartilhados.BarraTransicaoDeEscalas.aviso')"
  >
    <a
      class="barra-transicao__link"
      href="https://www.pontotel.com.br/lp/regularizador-de-escalas-e-jornadas-clientes#formulario"
      target="_blank"
      rel="noopener noreferrer"
    >
      <span class="barra-transicao__selo">
        <span class="barra-transicao__ponto" aria-hidden="true"></span>
        {{ t('compartilhados.BarraTransicaoDeEscalas.selo') }}
      </span>
      <span class="barra-transicao__mensagem">
        <strong>{{ t('compartilhados.BarraTransicaoDeEscalas.titulo') }}</strong>
        <span>{{ t('compartilhados.BarraTransicaoDeEscalas.descricao') }}</span>
      </span>
      <i18n-t
        v-if="diasRestantes > 0"
        keypath="compartilhados.BarraTransicaoDeEscalas.contagem"
        :plural="diasRestantes"
        tag="span"
        class="barra-transicao__contagem"
      >
        <template #dias><b>{{ diasRestantes }}</b></template>
      </i18n-t>
      <span class="barra-transicao__acao">
        {{ t('compartilhados.BarraTransicaoDeEscalas.prepararTransicao') }}
      </span>
    </a>
    <button
      type="button"
      class="barra-transicao__fechar"
      :aria-label="t('compartilhados.BarraTransicaoDeEscalas.fechar')"
      :title="t('compartilhados.BarraTransicaoDeEscalas.fechar')"
      @click="fechar"
    >
      <IconeSVG icone="icone-fechar" cor="currentColor" width="var(--espacamento-16)" height="var(--espacamento-16)" aria-hidden="true" />
    </button>
  </aside>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import IconeSVG from '@/common/IconeSVG.vue'
import { calcularDiasParaTransicao } from './contagemDaTransicao'

const { t } = useI18n()
const emitir = defineEmits<{ (evento: 'altura', altura: number): void }>()
const CHAVE_FECHADA = 'pontotel:barra-transicao-escalas:2026:fechada'
const barra = ref<HTMLElement | null>(null)
const fechada = ref(false)
const diasRestantes = ref(calcularDiasParaTransicao())
let observador: ResizeObserver | undefined
let temporizador: ReturnType<typeof setInterval> | undefined

function informarAltura() {
  emitir('altura', barra.value?.getBoundingClientRect().height ?? 0)
}

async function fechar() {
  fechada.value = true
  observador?.disconnect()
  clearInterval(temporizador)
  try {
    sessionStorage.setItem(CHAVE_FECHADA, '1')
  } catch {
    // O aviso continua dispensável quando o navegador bloqueia o armazenamento.
  }
  await nextTick()
  informarAltura()
}

onMounted(async () => {
  try {
    fechada.value = sessionStorage.getItem(CHAVE_FECHADA) === '1'
  } catch {
    // Sem armazenamento, mantém o aviso visível.
  }
  await nextTick()
  informarAltura()
  if (fechada.value || !barra.value) return
  observador = new ResizeObserver(informarAltura)
  observador.observe(barra.value)
  temporizador = setInterval(() => {
    diasRestantes.value = calcularDiasParaTransicao()
  }, 60_000)
})

onBeforeUnmount(() => {
  observador?.disconnect()
  clearInterval(temporizador)
})
</script>

<style scoped lang="scss">
.barra-transicao {
  position: fixed;
  inset: var(--shell-altura-aviso-usuario-suporte) 0 auto;
  z-index: 1000;
  display: flex;
  align-items: center;
  min-height: var(--espacamento-48);
  padding: var(--espacamento-8) var(--espacamento-40) var(--espacamento-8) var(--espacamento-16);
  background: var(--cor-secundaria-100);
  color: var(--cor-secundaria-contraste);
  font-family: var(--texto-tipografia);
  font-size: var(--texto-paragrafo-p-tamanho);
  font-weight: var(--texto-paragrafo-p-peso);
  line-height: var(--texto-paragrafo-p-entrelinha);

  &__link {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--espacamento-12);
    color: inherit;
    text-decoration: none;
  }

  &__mensagem {
    text-align: center;

    > span {
      display: none;
      margin-left: var(--espacamento-4);
    }
  }

  &__selo {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    gap: var(--espacamento-6);
    padding: var(--espacamento-3) var(--espacamento-10) var(--espacamento-3) var(--espacamento-8);
    border-radius: var(--arredondamento-max);
    background: color-mix(in srgb, var(--cor-secundaria-50) 65%, var(--cor-secundaria-100));
    color: var(--cor-primaria-100);
    font-size: var(--texto-legenda-pp-tamanho);
    font-weight: var(--texto-titulo-m-peso);
    line-height: var(--texto-legenda-pp-entrelinha);
    letter-spacing: 0.06em;
    text-transform: uppercase;
    white-space: nowrap;
  }

  &__ponto {
    flex-shrink: 0;
    width: var(--espacamento-7);
    height: var(--espacamento-7);
    border-radius: var(--arredondamento-max);
    background: var(--cor-primaria-100);
    animation: pulsar-selo 1.8s ease-out infinite;
  }

  strong,
  b,
  &__acao {
    font-weight: var(--texto-titulo-m-peso);
  }

  &__contagem {
    color: var(--cor-primaria-100);
    white-space: nowrap;

    b {
      color: var(--cor-primaria-100);
      font-size: var(--texto-titulo-m-tamanho);
      font-variant-numeric: tabular-nums;
    }
  }

  &__acao {
    display: none;
    border-bottom: var(--espessura-20) solid var(--cor-primaria-100);
    padding-bottom: var(--espacamento-2);
    text-transform: uppercase;
    white-space: nowrap;
  }

  &__fechar {
    position: absolute;
    top: 50%;
    right: var(--espacamento-8);
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: var(--espacamento-32);
    height: var(--espacamento-32);
    padding: 0;
    border: none;
    border-radius: var(--arredondamento-4);
    background: transparent;
    color: inherit;
    cursor: pointer;
  }

  &__link:hover &__acao {
    color: var(--cor-primaria-100);
  }

  &__fechar:hover {
    background: var(--cor-secundaria-200);
  }

  &__link:focus-visible,
  &__fechar:focus-visible {
    outline: var(--espessura-20) solid var(--cor-primaria-100);
    outline-offset: var(--espacamento-2);
  }

  @media (max-width: 479px) {
    padding-right: var(--espacamento-36);
    padding-left: var(--espacamento-12);

    &__link {
      gap: var(--espacamento-8);
    }

    &__fechar {
      right: var(--espacamento-2);
    }
  }

  @media (min-width: 768px) {
    &__contagem {
      padding: var(--espacamento-2) var(--espacamento-10);
      border: var(--espessura-10) solid var(--cor-secundaria-40);
      border-radius: var(--arredondamento-max);
      color: inherit;
    }

    &__acao {
      display: inline-flex;
    }
  }

  @media (min-width: 1100px) {
    &__mensagem > span {
      display: inline;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    &__ponto {
      animation: none;
    }
  }
}

@keyframes pulsar-selo {
  0% {
    box-shadow: 0 0 0 0 color-mix(in srgb, var(--cor-primaria-100) 60%, transparent);
  }

  70%,
  100% {
    box-shadow: 0 0 0 var(--espacamento-7) color-mix(in srgb, var(--cor-primaria-100) 0%, transparent);
  }
}
</style>
